if (!customElements.get('ks-testimonials-elite')) {
  customElements.define('ks-testimonials-elite', class KsTestimonialsElite extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slider = this.querySelector('[data-bn-kst-slider]');
      this.list = this.querySelector('[data-bn-kst-list]');
      this.slides = Array.from(this.querySelectorAll('[data-bn-kst-slide]'));
      this.nativePrev = this.querySelector('[data-bn-kst-native-controls] .slider-button--prev');
      this.nativeNext = this.querySelector('[data-bn-kst-native-controls] .slider-button--next');
      this.nativeCurrent = this.querySelector('[data-bn-kst-native-controls] .slider-counter--current');
      this.nativeTotal = this.querySelector('[data-bn-kst-native-controls] .slider-counter--total');
      this.controls = this.querySelector('[data-bn-kst-controls]');
      this.prev = this.querySelector('[data-bn-kst-prev]');
      this.next = this.querySelector('[data-bn-kst-next]');
      this.current = this.querySelector('[data-bn-kst-current]');
      this.total = this.querySelector('[data-bn-kst-total]');
      this.progress = this.querySelector('[data-bn-kst-progress]');
      this.autoplayTimer = null;
      this.inView = true;
      this.syncTimer = null;
      this.starBurstTimers = new Map();
      this.activeCardIndex = -1;

      this.style.setProperty('--ks-t-columns-desktop', this.dataset.columnsDesktop || this.getAttribute('data-columns-desktop') || '3');
      this.setupReveal();
      this.setupPagination();
      this.setupActiveCard();
      this.setupAutoplay();
      this.setupCardInteractions();
      this.setupThemeEditor();
      requestAnimationFrame(() => requestAnimationFrame(() => window.__bnPaginationVisibilityV3?.scan?.(this)));
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.entryObserver?.disconnect();
      this.activityObserver?.disconnect();
      this.counterObserver?.disconnect();
      this.stopAutoplay();
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.starBurstTimers?.forEach((timer) => clearTimeout(timer));
      this.starBurstTimers?.clear();
    }

    setupReveal() {
      if (!this.classList.contains('bn-kst-entry-armed')) return;
      const reveal = () => this.classList.add('bn-kst-in-view');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.isConnected) return;
        if (!('IntersectionObserver' in window)) { reveal(); return; }
        this.entryObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.classList.toggle('bn-kst-in-view', entry.isIntersecting);
        }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });
        this.entryObserver.observe(this);
      }));
    }

    setupPagination() {
      if (!this.controls || !this.nativePrev || !this.nativeNext) return;
      this.prev?.addEventListener('click', () => this.retreat(), { signal: this.abortController.signal });
      this.next?.addEventListener('click', () => this.advance(), { signal: this.abortController.signal });
      this.list?.addEventListener('scroll', () => this.scheduleSync(), { passive: true, signal: this.abortController.signal });
      if ((this.nativeCurrent || this.nativeTotal) && 'MutationObserver' in window) {
        this.counterObserver = new MutationObserver(() => this.syncPagination());
        if (this.nativeCurrent) this.counterObserver.observe(this.nativeCurrent, { childList: true, characterData: true, subtree: true });
        if (this.nativeTotal) this.counterObserver.observe(this.nativeTotal, { childList: true, characterData: true, subtree: true });
      }
      window.addEventListener('resize', () => this.scheduleSync(), { passive: true, signal: this.abortController.signal });
      this.syncPagination();
      requestAnimationFrame(() => requestAnimationFrame(() => this.syncPagination()));
    }

    scheduleSync() {
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.syncPagination();
        this.syncActiveCard();
      }, 70);
    }

    syncPagination() {
      const cur = Math.max(1, Number.parseInt(this.nativeCurrent?.textContent || '1', 10) || 1);
      const total = Math.max(cur, Number.parseInt(this.nativeTotal?.textContent || '1', 10) || 1);
      const hasPages = total > 1 && (this.list ? this.list.scrollWidth > this.list.clientWidth + 2 : true);
      this.controls.hidden = !hasPages;
      if (!hasPages) return;
      if (this.current) this.current.textContent = String(cur).padStart(2, '0');
      if (this.total) this.total.textContent = String(total).padStart(2, '0');
      if (this.progress) this.progress.style.width = `${Math.min(100, (cur / total) * 100)}%`;
      window.__bnPaginationVisibilityV3?.scan?.(this);
      window.__bnPaginationVisibilityV3?.evaluate?.();
    }

    retreat() {
      const cur = Number.parseInt(this.nativeCurrent?.textContent || '1', 10) || 1;
      if (this.nativePrev?.disabled || cur <= 1) {
        const maxScroll = Math.max(0, (this.list?.scrollWidth || 0) - (this.list?.clientWidth || 0));
        this.list?.scrollTo({ left: maxScroll, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
        setTimeout(() => this.scheduleSync(), 360);
      } else {
        this.nativePrev.click();
        this.scheduleSync();
      }
      this.restartAutoplay();
    }

    advance() {
      const cur = Number.parseInt(this.nativeCurrent?.textContent || '1', 10) || 1;
      const total = Number.parseInt(this.nativeTotal?.textContent || '1', 10) || 1;
      if (this.nativeNext?.disabled || cur >= total) {
        this.list?.scrollTo({ left: 0, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
        setTimeout(() => this.scheduleSync(), 360);
      } else {
        this.nativeNext.click();
        this.scheduleSync();
      }
      this.restartAutoplay();
    }

    setupActiveCard() {
      this.list?.addEventListener('scroll', () => this.syncActiveCard(), { passive: true, signal: this.abortController.signal });
      window.addEventListener('resize', () => this.syncActiveCard(), { passive: true, signal: this.abortController.signal });
      this.syncActiveCard();
    }

    syncActiveCard() {
      if (!this.list || !this.slides.length) return;
      const listRect = this.list.getBoundingClientRect();
      const center = listRect.left + listRect.width / 2;
      let activeIndex = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs((rect.left + rect.width / 2) - center);
        if (distance < bestDistance) { bestDistance = distance; activeIndex = index; }
      });
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === activeIndex);
        slide.classList.toggle('is-near', index === activeIndex - 1 || index === activeIndex + 1);
      });

      if (activeIndex !== this.activeCardIndex) {
        this.activeCardIndex = activeIndex;
        const activeCard = this.slides[activeIndex]?.querySelector('.ks-testimonials__card');
        if (activeCard) this.triggerStarBurst(activeCard);
      }
    }

    autoplayEnabled() {
      if (!this.controls || this.controls.hidden) return false;
      const width = window.innerWidth;
      if (width < 750) return this.dataset.autoplayMobile === 'true';
      if (width < 990) return this.dataset.autoplayTablet === 'true';
      return this.dataset.autoplayDesktop === 'true';
    }

    setupAutoplay() {
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: this.abortController.signal });
      window.addEventListener('resize', () => this.syncAutoplay(), { passive: true, signal: this.abortController.signal });
      if ('IntersectionObserver' in window) {
        this.inView = false;
        this.activityObserver = new IntersectionObserver((entries) => {
          this.inView = !!entries[0]?.isIntersecting;
          this.syncAutoplay();
        }, { threshold: 0.08 });
        this.activityObserver.observe(this);
      }
      this.syncAutoplay();
    }

    stopAutoplay() { if (this.autoplayTimer) clearTimeout(this.autoplayTimer); this.autoplayTimer = null; }
    restartAutoplay() { this.stopAutoplay(); this.syncAutoplay(); }
    syncAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled() || !this.inView || document.hidden) return;
      const delay = Math.max(4000, Number.parseInt(this.dataset.autoplaySpeed || '6000', 10));
      this.autoplayTimer = setTimeout(() => { this.autoplayTimer = null; this.advance(); this.syncAutoplay(); }, delay);
    }

    triggerStarBurst(card) {
      if (!card || this.prefersReducedMotion) return;
      const previousTimer = this.starBurstTimers?.get(card);
      if (previousTimer) clearTimeout(previousTimer);
      card.classList.remove('is-star-burst');
      void card.offsetWidth;
      card.classList.add('is-star-burst');
      const timer = setTimeout(() => {
        if (this.starBurstTimers?.get(card) !== timer) return;
        card.classList.remove('is-star-burst');
        this.starBurstTimers?.delete(card);
      }, 820);
      this.starBurstTimers?.set(card, timer);
    }

    setupCardInteractions() {
      this.slides.forEach((slide) => {
        const card = slide.querySelector('.ks-testimonials__card');
        if (!card) return;
        const start = (event) => {
          card.classList.add('is-touching');
          if (event.pointerType !== 'mouse') this.triggerStarBurst(card);
        };
        const end = () => card.classList.remove('is-touching');
        card.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          this.triggerStarBurst(card);
        }, { signal: this.abortController.signal });
        card.addEventListener('pointerdown', start, { signal: this.abortController.signal });
        card.addEventListener('pointerup', end, { signal: this.abortController.signal });
        card.addEventListener('pointercancel', end, { signal: this.abortController.signal });
        card.addEventListener('pointerleave', end, { signal: this.abortController.signal });
        card.addEventListener('focusin', () => this.triggerStarBurst(card), { signal: this.abortController.signal });
      });
    }

    setupThemeEditor() {
      this.addEventListener('shopify:section:select', () => {
        if (this.classList.contains('bn-kst-entry-armed')) {
          this.classList.remove('bn-kst-in-view');
          requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('bn-kst-in-view')));
        }
        this.scheduleSync();
        window.__bnPaginationVisibilityV3?.scan?.(this);
      }, { signal: this.abortController.signal });
      this.addEventListener('shopify:block:select', (event) => {
        const selected = event.target?.closest?.('[data-bn-kst-slide]');
        if (selected && this.list) {
          selected.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
          setTimeout(() => this.scheduleSync(), 300);
        }
      }, { signal: this.abortController.signal });
    }
  });
}
